/**
 * Pure math for dragging a block to another row/day: preserve span length, clamp to the
 * target job’s active dates, and build the PATCH body (`start_date`, `end_date`,
 * `schedule_group_id`, optional `job_id`).
 */
import {
  getDateInLocalTZFromDateStr,
  getShiftedDate,
  toLocalCalendarDay,
  toLocalDateKey,
} from "../../../../utils/datetime/core";
import { clampLocalDateToJobRange } from "./scheduleJobDateRange";

/**
 * Inclusive day count between two calendar days (stable with noon-normalized dates).
 *
 * @param {Date|string|number} startDate
 * @param {Date|string|number} endDate
 * @returns {number} At least 1.
 */
export function inclusiveCalendarDayCount(startDate, endDate) {
  const s = toLocalCalendarDay(startDate);
  const e = toLocalCalendarDay(endDate);
  let n = 0;
  const c = new Date(s.getTime());
  while (c.getTime() <= e.getTime()) {
    n += 1;
    c.setDate(c.getDate() + 1);
  }
  return Math.max(1, n);
}

/**
 * Whole calendar days from `startDate` (local, inclusive) to `targetDate` (local, inclusive),
 * treating same calendar day as 0. Used to align the grabbed day with the drop cell.
 *
 * @param {Date|string|number} startDate Block / run start
 * @param {Date|string|number} targetDate Day under pointer when drag started
 * @returns {number}
 */
export function calendarDaysFromBlockStart(startDate, targetDate) {
  const s = toLocalCalendarDay(startDate);
  const t = toLocalCalendarDay(targetDate);
  if (t.getTime() < s.getTime()) {
    return 0;
  }
  let n = 0;
  const c = new Date(s.getTime());
  while (c.getTime() < t.getTime()) {
    n += 1;
    c.setDate(c.getDate() + 1);
  }
  return n;
}

/**
 * Drop on a row + day: place the block so the grabbed calendar day (if any) lands on the
 * drop cell; otherwise anchor start to the drop day. Preserves span length and sets group from row.
 *
 * @param {Object} params
 * @param {Object} params.block WorkScopeAspectsBlock-shaped
 * @param {Object} params.targetRow Schedule flat row
 * @param {Date} params.dropDay Column day
 * @param {Object|null} params.targetJobRaw API-shaped job row for date clamp (target project)
 * @param {Object|null} [params.groupMeta] `{ id }` when target is a group row
 * @param {string|number|null} [params.targetJobId] Sent as `job_id` on PATCH (drop target project)
 * @param {{ id: unknown, number?: string, description?: string }} params.nextBlockJob `job` fields for optimistic state
 * @param {string|null} [params.grabDayKey] `YYYY-MM-DD` of the day column where the drag started (omit = anchor start to drop)
 * @returns {{ nextBlock: Object, patchBody: Object }} Optimistic block and PATCH body for {@link patchScheduleBlock}.
 */
export function computeBlockMoveAfterDrop({
  block,
  targetRow,
  dropDay,
  targetJobRaw,
  groupMeta = null,
  targetJobId = null,
  nextBlockJob,
  grabDayKey = null,
}) {
  const spanDays = inclusiveCalendarDayCount(block.startDate, block.endDate);
  const grabDay =
    grabDayKey != null && String(grabDayKey).trim() !== ""
      ? toLocalCalendarDay(
          getDateInLocalTZFromDateStr(String(grabDayKey).split("T")[0])
        )
      : null;
  let grabOffsetDays = 0;
  if (grabDay) {
    grabOffsetDays = calendarDaysFromBlockStart(block.startDate, grabDay);
    grabOffsetDays = Math.max(0, Math.min(spanDays - 1, grabOffsetDays));
  }

  let newStart = toLocalCalendarDay(dropDay);
  newStart = getShiftedDate(newStart, -grabOffsetDays);
  if (targetJobRaw) {
    newStart = clampLocalDateToJobRange(newStart, targetJobRaw);
  }
  let newEnd = getShiftedDate(newStart, spanDays - 1);
  if (targetJobRaw) {
    newEnd = clampLocalDateToJobRange(newEnd, targetJobRaw);
  }
  if (newStart.getTime() > newEnd.getTime()) {
    newEnd = new Date(newStart.getTime());
  }

  const scheduleGroup =
    targetRow.rowType === "group"
      ? groupMeta ?? { id: targetRow.groupId }
      : null;

  const nextBlock = {
    ...block,
    job: {
      id: nextBlockJob.id,
      number: nextBlockJob.number ?? "",
      description: nextBlockJob.description ?? "",
    },
    startDate: newStart,
    endDate: newEnd,
    scheduleGroup,
  };

  const scheduleGroupId =
    targetRow.rowType === "group" ? (targetRow.groupId ?? null) : null;

  const patchBody = {
    start_date: toLocalDateKey(newStart),
    end_date: toLocalDateKey(newEnd),
    schedule_group_id: scheduleGroupId,
    /** Backend: slot-based aspect resync so crews move with the block (see `preserve_calendar_days`). */
    preserve_calendar_days: false,
  };
  if (targetJobId != null && targetJobId !== "") {
    patchBody.job_id = targetJobId;
  }

  return { nextBlock, patchBody };
}
